import FieldValidator from './FieldValidator';

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export default class ValidateFields {
    private form: HTMLFormElement;
    private hasErrors = true;

    constructor(form: HTMLFormElement) {
        this.form = form;

        this.hasErrors = this.validateFields(
            Array.from(this.form.querySelectorAll<FormField>('input,select,textarea')),
            true
        );

        this.onLoad();
    }

    private onLoad(): void {
        const fields = this.form.querySelectorAll<FormField>('input,select,textarea');

        fields.forEach(field =>
            field.addEventListener('change', this.onChange.bind(this))
        );

        const submitButtons = this.form.querySelectorAll<HTMLButtonElement>(
            "button[type='submit']"
        );

        submitButtons.forEach(button =>
            button.addEventListener('click', this.onSubmit.bind(this))
        );
    }

    private onChange(event: Event): void {
        const field = event.currentTarget as FormField;
        if (!field) return;

        if (!this.validateField(field)) {
            this.hasErrors = this.validateFields(
                Array.from(this.form.querySelectorAll<FormField>('input,select,textarea')),
                true
            );

            if (!this.hasErrors) {
                this.onSubmitErrors();
            }
        }
    }

    private onSubmit(event: Event): void {
        const fields = this.form.querySelectorAll<FormField>('input,select,textarea');

        this.hasErrors = this.validateFields(Array.from(fields));

        if (this.hasErrors) {
            event.preventDefault();
        }

        this.onSubmitErrors();
    }

    private onSubmitErrors(): void {
        const buttons = this.form.querySelectorAll<HTMLButtonElement>(
            "button[type='submit']"
        );

        buttons.forEach(button => {
            const parent = this.searchForParent(button, 'field');
            const messages = parent?.querySelectorAll<HTMLElement>(
                '.validation-error-message'
            ) ?? [];

            if (this.hasErrors) {
                button.classList.add('is-danger');
                button.classList.remove('is-success');
                messages.forEach(m => {
                    m.classList.remove('hidden');
                    m.classList.add('visible');
                });
            } else {
                button.classList.add('is-success');
                button.classList.remove('is-danger');
                messages.forEach(m => {
                    m.classList.remove('visible');
                    m.classList.add('hidden');
                });
            }
        });
    }

    private validateFields(fields: FormField[], silently = false): boolean {
        let hasErrors = false;

        fields.forEach(field => {
            const fieldError = this.validateField(field, silently);
            hasErrors = hasErrors || fieldError;
        });

        return hasErrors;
    }

    private validateField(field: FormField, silently = false): boolean {
        const attrValue = field.dataset.validation ?? '';
        const validator = this.getValidatorFunction(attrValue);

        const value = field.value ?? '';
        const parent = this.searchForParent(field, 'field');
        const messages = parent?.querySelectorAll<HTMLElement>(
            '.validation-error-message'
        ) ?? [];

        const isValid = validator(value);

        if (!isValid) {
            if (!silently) {
                field.classList.add('is-danger');
                field.classList.remove('is-success');
                messages.forEach(m => {
                    m.classList.remove('hidden');
                    m.classList.add('visible');
                });
            }
            return true;
        }

        if (!silently) {
            field.classList.add('is-success');
            field.classList.remove('is-danger');
            messages.forEach(m => {
                m.classList.remove('visible');
                m.classList.add('hidden');
            });
        }

        return false;
    }

    private searchForParent(element: HTMLElement, className: string): HTMLElement | null {
        let current: HTMLElement | null = element;

        while (current && current.parentElement) {
            current = current.parentElement;
            if (current.classList.contains(className)) {
                return current;
            }
        }

        return null;
    }

    private getValidatorFunction(dataValidationAttr: string) {
        const validators = dataValidationAttr.split(' ').filter(Boolean);

        const functions = validators.map(validator => {
            switch (validator) {
                case 'not-blank':
                    return FieldValidator.notBlank;
                case 'email':
                    return FieldValidator.email;
                case 'phone':
                    return FieldValidator.phone;
                case 'number':
                    return FieldValidator.number;
                default:
                    return () => true;
            }
        });

        return (value: string): boolean =>
            functions.map(fn => fn(value)).every(Boolean);
    }
}
